/** The origin a STUDENT should use, which is not always the one this page is served from.
 *
 * The Teacher Computer app opens its windows on `http://127.0.0.1:<port>` — the shell
 * navigates there, and so does the projector window. Any join link built from
 * `location.origin` therefore reads `127.0.0.1` on the board, and a phone that scans it asks
 * its own loopback and fails. The sidecar listens on every interface, so the fix is to ask it
 * which address a phone can reach (`/v1/host/info`, unauthenticated) and use that instead.
 *
 * Only a loopback page is substituted. A teacher who opened the host over the network already
 * has a reachable origin, and on a real server there is no host info to ask.
 */
import { useEffect, useState } from "react";

import { serviceUrl } from "../api/base";
import { nsKey } from "./storage";

const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);
const PICKED = nsKey("room.address");

/** The address the teacher chose on Room setup, when the host still reports it. */
export function pickedAddress(): string | null {
  try {
    return localStorage.getItem(PICKED);
  } catch {
    return null;
  }
}

export function pickAddress(address: string | null): void {
  if (address) localStorage.setItem(PICKED, address);
  else localStorage.removeItem(PICKED);
  pending = null; // the next reader re-resolves
  pendingInfo = null;
}

/** True when this page is served from the machine it runs on (so its origin is not shareable). */
export function servedFromLoopback(): boolean {
  return LOOPBACK.has(location.hostname);
}

let pending: Promise<string> | null = null;
let pendingCloud: Promise<string | null> | null = null;

export type HostInfoLite = { local?: boolean; port?: number; addresses?: string[]; cloud?: string | null };

let pendingInfo: Promise<HostInfoLite | null> | null = null;

async function fetchHostInfo(): Promise<HostInfoLite | null> {
  try {
    const res = await fetch(serviceUrl("/session/v1/host/info"), { signal: AbortSignal.timeout(4000) });
    return res.ok ? ((await res.json()) as HostInfoLite) : null;
  } catch {
    return null;
  }
}

/** The host's word about itself, asked once per page: `local: true` from the Teacher
 * Computer app's sidecar, null on a real server where the route does not exist. Shared by
 * every reader (the join address, the cloud origin, `live/host.ts`) so the page asks once. */
export function hostInfo(): Promise<HostInfoLite | null> {
  pendingInfo ??= fetchHostInfo();
  return pendingInfo;
}

async function resolve(): Promise<string> {
  if (!servedFromLoopback()) return location.origin;
  const info = await hostInfo(); // no local host to ask: the page's own origin is the answer
  const offered = info?.local === true ? (info.addresses ?? []) : [];
  // A machine on a VPN offers several addresses and the first is only a guess; the teacher's
  // choice on Room setup wins, as long as the host still has that address.
  const chosen = pickedAddress();
  const address = chosen && offered.includes(chosen) ? chosen : offered[0];
  return address ? `http://${address}:${info?.port ?? location.port}` : location.origin;
}

/** Where an invitation must point: the SERVER, which is not the room's host.
 *
 * An invite is opened by someone who is not in this room, so the local address is wrong for
 * it. Inside the Teacher Computer app the answer is the configured cloud origin; without one
 * there is nobody to invite yet and the caller shows nothing.
 */
async function resolveCloud(): Promise<string | null> {
  if (!servedFromLoopback()) return location.origin;
  const info = await hostInfo();
  return info?.cloud ? info.cloud.replace(/\/+$/, "") : null;
}

/** Cached across screens: the address does not change while the app is open. */
export function joinOrigin(): Promise<string> {
  pending ??= resolve();
  return pending;
}

/** `location.origin` until the host answers, then the address students can reach. */
export function useJoinOrigin(): string {
  const [origin, setOrigin] = useState(() => (servedFromLoopback() ? "" : location.origin));
  useEffect(() => {
    let live = true;
    void joinOrigin().then((o) => {
      if (live) setOrigin(o);
    });
    return () => {
      live = false;
    };
  }, []);
  return origin;
}

/** The server origin for links that leave this network, or null while unknown or unset. */
export function useCloudOrigin(): string | null {
  const [origin, setOrigin] = useState<string | null>(() => (servedFromLoopback() ? null : location.origin));
  useEffect(() => {
    let live = true;
    pendingCloud ??= resolveCloud();
    void pendingCloud.then((o) => {
      if (live) setOrigin(o);
    });
    return () => {
      live = false;
    };
  }, []);
  return origin;
}
